import Vue from "vue";
import { mapGetters } from "vuex";

import {
    GridUtil,
    ObjectUtil,
    PartDetailServices,
    EventTrackingService
} from "@/services";

import {
    CurrencyText,
    LoadingSpinner,
    PartDetailsLink,
    StaticFormControl
} from "@/components";

import { DlTable } from "@/components/Elements/DlTable";

import { salesOrgHelper } from "@/components/mixins/salesOrgHelper"

import { PartValidationReasonIds } from "@/types";

import {
    IColumnConfig,
    FreezeStatus,
    DlTableRow
} from "@/types";

import { default as AddCrossReference } from "@/components/PartDetail/Dialogs/AddCrossReference/AddCrossReference.vue"
import { default as BreakCrossReference } from "@/components/PartDetail/Dialogs/BreakCrossReference/BreakCrossReference.vue";
import { default as SuggestPartApplication }             from "@/components/PartDetail/Dialogs/SuggestPartApplication/SuggestPartApplication.vue";

import { default as RequestMoreInfo }             from "@/components/PartDetail/Dialogs/RequestMoreInfo/RequestMoreInfo.vue";
import DetailsAndPricing from "../../DetailsAndPricing/DetailsAndPricing";

export default Vue.extend({
    name: "GeneralTab",
    components: {
        CurrencyText,
        LoadingSpinner,
        PartDetailsLink,
        StaticFormControl,
        AddCrossReference,
        BreakCrossReference,
        SuggestPartApplication,
        RequestMoreInfo,
        DlTable
    },
    mixins: [salesOrgHelper],
    computed: {
        ...mapGetters([
            "getContentItem",
            "activeAccountId",
            "user",
            "audienceIds",
            "IsMcfa",
            "canSeeContractPrice",
            "canSeeDealerNet",
            "isPriceVisible",
            "McfCostCenterId"
        ]),
        marketingDescription() {
              return this.details.MarketingDescription;
        },
        showContractPricingMessage() {
            var self = this;
            var shouldShowContractPricingMessage = false;
            this.partAlternatives.forEach(function (partAlt: any) {
                var priceInfo = self.getAlternativePriceInfo(partAlt);
                if (priceInfo.Price && priceInfo.Type === 'Contract') {
                    shouldShowContractPricingMessage = true;
                }
            })

            return shouldShowContractPricingMessage;
        },
        generalInfoDlTableRows() {
            var rows = [];

            var rowSapReturnable = new DlTableRow();
            rowSapReturnable.key = this.getContentItem('Returnable');
            rowSapReturnable.value = this.details.IsSapReturnable ? 'Yes' : 'No';
            rows.push(rowSapReturnable);

            if (this.details.SalesOrganization) {
                var rowSalesOrg = new DlTableRow();
                rowSalesOrg.key = this.getContentItem('SourceOfSupply');
                var SourceOfSupply = this.details.SalesOrganization == "MA" ? this.details.ProvisionCode : this.details.SalesOrganization;                 
                rowSalesOrg.value = SourceOfSupply;
                rows.push(rowSalesOrg);
            }

            if (this.emergencyFeeIsVisible()) {
                var rowEmergencyFee = new DlTableRow();
                rowEmergencyFee.key = this.getContentItem('EmergencyFee');
                rowEmergencyFee.value = (this.details.EmergencyFeePercentage * 100) + "%";
                rows.push(rowEmergencyFee);
            }

            if (this.details.HasMsdsInfo) {
                var rowMsds = new DlTableRow();
                rowMsds.key = "MSDS Sheet"
                rowMsds.valueIsHtml = true;
                rowMsds.value = `
                    <a 
                        href="${window.appGlobals.PART_IMAGES_API_URL}Media/GetMsds/${this.details.OemPartCode}" 
                        target="_blank">
                        Click here for MSDS
                    </a>`
                rows.push(rowMsds);
            }

            var rowHazardous = new DlTableRow();
            rowHazardous.key = this.getContentItem('Hazardous');
            rowHazardous.value = this.details.IsHazardous ? 'Yes' : 'No';
            rows.push(rowHazardous);

            return rows;
        },
        coreReturnPeriodRows() {
            var rows = [];

            if (this.details.CoreReturnPeriod.StockMonths) {
                var rowStockMonths = new DlTableRow();
                rowStockMonths.key = this.getContentItem('StockOrder');
                rowStockMonths.value = `${this.details.CoreReturnPeriod.StockMonths} ${this.getContentItem('Months')}`;
                rows.push(rowStockMonths);
            }
            if (this.details.CoreReturnPeriod.EmergencyMonths) {
                var rowEmergencyMonths = new DlTableRow();
                rowEmergencyMonths.key = this.getContentItem('EmergencyOrder');
                rowEmergencyMonths.value = `${this.details.CoreReturnPeriod.EmergencyMonths} ${this.getContentItem('Months')}`;
                rows.push(rowEmergencyMonths);
            }
            return rows;
        },
        formTypes(){
            var formTypes = [
                { value: null, text: '-- Select a Request Type --' },
                { value: 'RequestMoreInfo', text: 'Request More Information' },
                { value: 'SuggestBreakCross', text: 'Suggest we break a cross' },
                { value: 'SuggestAddCross', text: 'Suggest we add a cross' },
                { value: 'SuggestPartApp', text: 'Suggest a part application' }
            ]
            if(this.details.Images[0] == "NoImageAvailable")
            {
                var requestPartPicturesFormType = { value: 'RequestPartPictures', text: 'Request Part Pictures' };
                //insert this into formTypes at index of 2
                formTypes.splice(2, 0, requestPartPicturesFormType);
            }
            return formTypes;
        }
    },
    methods: {
        emergencyFeeIsVisible() {
            return !this.details.IsInvalidPartWithNprAvailable;
        },
        isPartsFastPartsFreeEligible() {
            return !(
                this.details.IsHazardous ||
                this.details.IsMadeAsOrdered ||
                this.details.IsVendorNonStockProgram ||
                this.details.FreezeStatus === FreezeStatus.H ||
                this.details.FreezeStatus === FreezeStatus.S ||
                this.details.ItemCategory === "ZDSH"
            );
        },
        coreReturnPeriod() {
            if (this.details.CoreReturnPeriod) {
                return this.details.CoreReturnPeriod.StockMonths || this.details.CoreReturnPeriod.EmergencyMonths;
            }
        },
        async getPartAlternatives() {
            this.partAlterativesAreLoading = true;
            var validateAlternatives = true;
            var partValidationReason = this.details.PartStatusMessage.PartValidationReason;
            if(["IsDiscontinued","IsSuperseded"].includes(partValidationReason))
            {
                validateAlternatives = false;
            }
            var requestParams = {
                accountId: this.activeAccountId,
                userId: this.user.UserId,
                partId: this.details.Id,
                oemPartCode: this.details.OemPartCode,
                userAudiences: this.audienceIds,
                validateAlternatives: validateAlternatives
            }
            this.partAlternatives = await PartDetailServices.getPartAlternatives(requestParams);
            this.partAlterativesAreLoading = false;
        },
        shouldHidePricingForPartAlternative(partAlternative: any) {
            var hide = false;
            switch (partAlternative.ValidationReason) {
                case PartValidationReasonIds.IsChannelNotAuthorized:
                case PartValidationReasonIds.IsCanceled:
                case PartValidationReasonIds.IsSuperseded:
                case PartValidationReasonIds.IsDiscontinued:
                case PartValidationReasonIds.ItemNotPriced:
                case PartValidationReasonIds.IsMaoNoPrice:
                case PartValidationReasonIds.IsMaoLimitExceeded:
                case PartValidationReasonIds.IsHardFrozen:
                case PartValidationReasonIds.IsMaoLimitExceededPriceNotCurrent:
                case PartValidationReasonIds.IsMaoLimitExceededPriceNotCurrentNoStockQuantity:
                case PartValidationReasonIds.IsMaoLimitExceededNoStockQuantity:
                case PartValidationReasonIds.MaoSoftFrozen:
                    hide = true;
                    break;
                case PartValidationReasonIds.IsSoftFrozen:
                    var lastCharOfAlternative = partAlternative.AlternativeOemPartCode.slice(-1);
                    if (this.IsMcfa && partAlternative.AlternativePicCode === "608" && !["A", "B", "C"].includes(lastCharOfAlternative)) {
                        hide = true;
                    }
                    break;
            }
            return hide;
        },
        getAlternativePriceInfo(partAlternative: any) {

            var altPriceInfo = {};
            if (this.canSeeContractPrice && this.canSeeDealerNet) {
                if (
                    partAlternative.ContractPrice &&
                    partAlternative.DealerNetPrice &&
                    partAlternative.ContractPrice > 0 &&
                    partAlternative.ContractPrice < partAlternative.DealerNetPrice
                ) {
                    altPriceInfo = { Type: 'Contract', Price: partAlternative.ContractPrice }
                }
                altPriceInfo = { Type: 'DealerNet', Price: partAlternative.DealerNetPrice }
            }
            else if (this.canSeeContractPrice) {
                altPriceInfo = { Type: 'Contract', Price: partAlternative.ContractPrice };
            }
            else if (this.canSeeDealerNet) {
                altPriceInfo = { Type: 'DealerNet', Price: partAlternative.DealerNetPrice }
            }

            return altPriceInfo;
        },
        hideForms() {
            this.formTypeToShow = null;
            this.showFormDropdown = false;
        },
        trackAlternativeClick(item){
            EventTrackingService.trackEvent({
                category: "PartDetail-Alternatives",
                action: "ClickedPartAlternativeLink",
                label: `SourcePartId - ${item.SourcePartId}, AlternativePartId - ${item.AlternativePartId}`,
                value: 1
            });
        }

    },
    props: {
        details: {
            type: Object,
            default: () => {},
        },
    },

    data: () => ({
        partAltTableColumns: [
            { key: "OemPartCode", label: "Part Number" },
            { key: "PartType", label: "Replacement Type" },
            { key: "Price", label: "Price" },
        ],
        partAlternatives: [],
        partAlterativesAreLoading: true,
        showFormDropdown: false,
        formTypeToShow: null
    }),

    created() {
        this.partAltTableColumns = GridUtil.localizeColumnLabels(this.partAltTableColumns, this.getContentItem);
        this.getPartAlternatives();
    },
});
